import React, { useEffect, useRef } from "react";

import { mediaQueriesV2 } from "@ui/themes/default";
import { TestWrapper } from "@ui/Atoms/TestWrapper";

import { Props } from "./Image.types";
import * as Styled from "./Image.styled";
import { areStaticSources, isNotMediaQuery } from "./Image.helpers";

/**
 * To generate base64 use https://evanw.github.io/thumbhash/
 * select TumbHash canvas and use $0.toDataURL() in console
 * or copy a TumbHash's src from images list
 */
export const Image: React.FC<Props> = props => {
  const lazyLoadingEnabled = props.loading === "lazy";
  const $image = useRef<HTMLImageElement>(null);
  const $base64 = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!lazyLoadingEnabled) return;
    if (!$image.current) return;
    if (!$base64.current && !!props.base64) return;

    const handleBase64 = (base64: HTMLImageElement) => {
      if (base64.src?.includes("data:image")) {
        base64.style.opacity = "0";
      }
    };

    if ($image.current.complete) {
      $image.current.style.opacity = "1";
      $base64.current && handleBase64($base64.current);
      return;
    }

    $image.current.addEventListener(
      "load",
      () => {
        $image.current && ($image.current.style.opacity = "1");
        $base64.current && handleBase64($base64.current);
      },
      { once: true },
    );
  }, [lazyLoadingEnabled, props.base64]);

  return (
    <TestWrapper testId={props.testId}>
      <Styled.Root
        className={props.className}
        $width={props.width}
        $height={props.height}
      >
        <picture data-fade-in={lazyLoadingEnabled}>
          {!!props.sources && !areStaticSources(props.sources) && props.sources}

          {!!props.sources &&
            areStaticSources(props.sources) &&
            props.sources.map((source, index) => (
              <source
                key={index}
                srcSet={source.srcSet}
                media={
                  isNotMediaQuery(source.media)
                    ? source.media
                    : mediaQueriesV2[source.media]
                }
              />
            ))}

          <Styled.Image
            ref={$image}
            src={props.src}
            alt={props.alt}
            loading={props.loading}
            $objectFit={props.objectFit}
            $fadeInAnimation={lazyLoadingEnabled}
          />
        </picture>

        {!!props.base64 && (
          <Styled.Base64
            ref={$base64}
            src={props.base64}
            decoding={lazyLoadingEnabled ? "async" : "sync"}
            $objectFit={props.objectFit}
            aria-hidden
          />
        )}
      </Styled.Root>
    </TestWrapper>
  );
};
